import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { InfoTooltip } from '../../components/ui/info-tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Building,
  Users,
  TrendingUp,
  ArrowRight,
  Loader2,
  Shield,
  UserPlus,
  Truck,
  ClipboardList,
  Activity,
  Calendar,
  ChevronDown,
  ChevronUp,
  BarChart3,
  PieChart as PieChartIcon,
  CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = ['#635bff', '#00d4aa', '#ff6b8a', '#ffa726', '#7b68ee', '#00bfff'];

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, iconColor, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-neutral-50 hover:bg-neutral-100 transition-colors"
        data-testid={`toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <div className={`h-8 w-8 rounded-lg ${iconColor} flex items-center justify-center`}>
            <Icon className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-medium text-neutral-900">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-neutral-400" strokeWidth={1.5} />
        ) : (
          <ChevronDown className="h-5 w-5 text-neutral-400" strokeWidth={1.5} />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

// Clickable Stat Card Component
const ClickableStatCard = ({ to, icon: Icon, iconBgColor, value, label, tooltip, valueColor = 'text-black' }) => (
  <Link to={to} data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <Card className="border-neutral-200 shadow-sm hover:shadow-md hover:border-neutral-300 transition-all cursor-pointer">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg ${iconBgColor} flex items-center justify-center`}>
            <Icon className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <p className={`text-2xl font-semibold ${valueColor}`}>{value}</p>
            <div className="flex items-center gap-1">
              <p className="text-xs text-neutral-500">{label}</p>
              {tooltip && <InfoTooltip content={tooltip} />}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

const AdminDashboard = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [timePeriod, setTimePeriod] = useState('6m');
  const [chartData, setChartData] = useState({
    registrations: [],
    supplierStatus: [],
    marketplaceActivity: [],
    userRoles: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, dashboardRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/dashboard-charts').catch(() => ({ data: null }))
        ]);
        
        setStats(statsRes.data);
        
        if (dashboardRes.data) {
          setChartData(dashboardRes.data);
        } else {
          // Generate mock data for visualization
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun'];
          setChartData({
            registrations: months.map((month, i) => ({
              month,
              organisaties: Math.floor(Math.random() * 10) + (i + 1) * 2,
              gebruikers: Math.floor(Math.random() * 20) + (i + 1) * 5
            })),
            supplierStatus: [
              { name: 'Goedgekeurd', value: statsRes.data?.suppliers?.approved || 2, color: '#00d4aa' },
              { name: 'In behandeling', value: statsRes.data?.suppliers?.pending || 1, color: '#ffa726' },
              { name: 'Geschorst', value: statsRes.data?.suppliers?.suspended || 0, color: '#ff6b8a' }
            ],
            marketplaceActivity: months.map((month, i) => ({
              month,
              aanvragen: Math.floor(Math.random() * 15) + i * 2,
              leads: Math.floor(Math.random() * 10) + i
            })),
            userRoles: [
              { role: 'Klanten', count: statsRes.data?.users_by_role?.user || 5 },
              { role: 'Partners', count: statsRes.data?.users_by_role?.partner || 3 },
              { role: 'Admins', count: statsRes.data?.users_by_role?.admin || 1 },
              { role: 'Leveranciers', count: statsRes.data?.suppliers?.total || 2 }
            ]
          });
        }
      } catch (error) {
        console.error('Dashboard load error:', error);
        toast.error('Data laden mislukt');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="border-neutral-200"
              onClick={() => window.location.reload()}
              data-testid="refresh-admin-dashboard"
            >
              <Activity className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <span className="text-sm text-neutral-500">
              {new Date().toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Stats Grid - Clickable Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <ClickableStatCard
            to="/admin/organizations"
            icon={Building}
            iconBgColor="bg-[#635bff]"
            value={stats?.organizations || 0}
            label="Organisaties"
            tooltip="Totaal aantal geregistreerde organisaties op het platform"
          />

          <ClickableStatCard
            to="/admin/organizations"
            icon={Shield}
            iconBgColor="bg-[#00d4aa]"
            value={stats?.avg_ggrc_score || 0}
            label="Gem. CWI"
            tooltip="Cyber Weerbaarheids Index - gemiddelde score van alle organisaties (0-100). Groen: 70+, Oranje: 50-69, Rood: onder 50."
            valueColor={(stats?.avg_ggrc_score || 0) >= 70 ? 'text-green-600' : (stats?.avg_ggrc_score || 0) >= 50 ? 'text-orange-500' : 'text-red-500'}
          />

          <ClickableStatCard
            to="/admin/users"
            icon={Users}
            iconBgColor="bg-[#7b68ee]"
            value={stats?.users || 0}
            label="Gebruikers"
            tooltip="Totaal aantal actieve gebruikers (klanten, admins, partners)"
          />

          <ClickableStatCard
            to="/admin/leveranciers"
            icon={Truck}
            iconBgColor="bg-[#ff6b8a]"
            value={stats?.suppliers?.total || stats?.partners?.total || 0}
            label="Leveranciers"
            tooltip="Geregistreerde leveranciers in het marketplace ecosysteem"
          />

          <ClickableStatCard
            to="/admin/marketplace"
            icon={ClipboardList}
            iconBgColor="bg-[#ffa726]"
            value={stats?.marketplace_requests || 0}
            label="Aanvragen"
            tooltip="Totaal aantal marketplace hulpaanvragen van klanten"
          />

          <ClickableStatCard
            to="/admin/aanmeldingen"
            icon={UserPlus}
            iconBgColor="bg-[#00bfff]"
            value={stats?.pending_signups || 0}
            label="Aanmeldingen"
            tooltip="Nieuwe aanmeldingen die wachten op goedkeuring"
          />
        </div>

        {/* Time Period Filter for Charts */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">Statistieken & Trends</h2>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-neutral-400" />
            <Select value={timePeriod} onValueChange={setTimePeriod}>
              <SelectTrigger className="w-[140px] h-9" data-testid="time-period-filter">
                <SelectValue placeholder="Periode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3m">3 maanden</SelectItem>
                <SelectItem value="6m">6 maanden</SelectItem>
                <SelectItem value="12m">12 maanden</SelectItem>
                <SelectItem value="all">Alles</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Charts Grid - Grouped in Collapsible Sections */}
        <div className="space-y-6">
          {/* Growth & Activity Charts */}
          <CollapsibleSection 
            title="Groei & Activiteit" 
            icon={Activity} 
            iconColor="bg-[#635bff]"
            defaultOpen={true}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Registrations Over Time */}
              <Card className="border-neutral-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                    <Activity className="h-4 w-4 text-[#635bff]" />
                    Registraties per maand
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Organisaties en gebruikers groei
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 min-w-0 overflow-hidden">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData.registrations}>
                        <defs>
                          <linearGradient id="colorOrgs" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#635bff" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#635bff" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00d4aa" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#00d4aa" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#999" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#999" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }} 
                        />
                        <Area 
                          type="monotone" 
                          dataKey="organisaties" 
                          stroke="#635bff" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorOrgs)" 
                          name="Organisaties"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="gebruikers" 
                          stroke="#00d4aa" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorUsers)" 
                          name="Gebruikers"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Marketplace Activity */}
              <Card className="border-neutral-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-[#ffa726]" />
                    Marketplace activiteit
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Aanvragen en geaccepteerde leads
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 min-w-0 overflow-hidden">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.marketplaceActivity}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#999" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#999" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                        />
                        <Bar 
                          dataKey="aanvragen" 
                          name="Aanvragen" 
                          fill="#ffa726" 
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar 
                          dataKey="leads" 
                          name="Leads" 
                          fill="#635bff" 
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CollapsibleSection>

          {/* Distribution Charts */}
          <CollapsibleSection 
            title="Verdelingen" 
            icon={PieChartIcon} 
            iconColor="bg-[#ff6b8a]"
            defaultOpen={true}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Supplier Status Distribution */}
              <Card className="border-neutral-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                    <Truck className="h-4 w-4 text-[#ff6b8a]" />
                    Leveranciers per status
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Verdeling van leverancier statussen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.supplierStatus}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {chartData.supplierStatus.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => [value, name]}
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          formatter={(value) => <span className="text-sm text-neutral-600">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Users by Role */}
              <Card className="border-neutral-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#7b68ee]" />
                    Gebruikers per rol
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Verdeling van gebruikerstypes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 min-w-0 overflow-hidden">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.userRoles} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 12 }} stroke="#999" />
                        <YAxis 
                          type="category" 
                          dataKey="role" 
                          tick={{ fontSize: 12 }} 
                          stroke="#999"
                          width={80}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e5e5',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                        />
                        <Bar 
                          dataKey="count" 
                          name="Aantal" 
                          radius={[0, 4, 4, 0]}
                        >
                          {chartData.userRoles.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CollapsibleSection>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/organizations" data-testid="quick-link-organizations">
            <Card className="border-neutral-200 hover:shadow-md hover:border-[#635bff]/30 transition-all cursor-pointer">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-[#635bff]/10 flex items-center justify-center">
                  <Building className="h-5 w-5 text-[#635bff]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-black">Organisaties</p>
                  <p className="text-xs text-neutral-500">Beheer & CWI scores</p>
                </div>
                <ArrowRight className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/users" data-testid="quick-link-users">
            <Card className="border-neutral-200 hover:shadow-md hover:border-[#7b68ee]/30 transition-all cursor-pointer">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-[#7b68ee]/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-[#7b68ee]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-black">Gebruikers</p>
                  <p className="text-xs text-neutral-500">Beheer & rollen</p>
                </div>
                <ArrowRight className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/marketplace" data-testid="quick-link-marketplace">
            <Card className="border-neutral-200 hover:shadow-md hover:border-[#ff6b8a]/30 transition-all cursor-pointer">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-[#ff6b8a]/10 flex items-center justify-center">
                  <Truck className="h-5 w-5 text-[#ff6b8a]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-black">Marketplace</p>
                  <p className="text-xs text-neutral-500">Leveranciers & leads</p>
                </div>
                <ArrowRight className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/aanmeldingen" data-testid="quick-link-aanmeldingen">
            <Card className="border-neutral-200 hover:shadow-md hover:border-[#00bfff]/30 transition-all cursor-pointer">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-[#00bfff]/10 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-[#00bfff]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-black">Aanmeldingen</p>
                  <p className="text-xs text-neutral-500">
                    {(stats?.pending_signups || 0) > 0 ? (
                      <Badge className="bg-orange-100 text-orange-700 text-xs font-medium">
                        {stats?.pending_signups} nieuw
                      </Badge>
                    ) : 'Nieuwe accounts'}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
